import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import axios from 'axios';
import { toast } from 'sonner';

// Import components
import AdminHeader from '../../components/admin/AdminHeader';
import AdminTabs from '../../components/admin/AdminTabs';
import PasswordModal from '../../components/admin/PasswordModal';
import ContentTab from '../../components/admin/ContentTab';
import StudentsTab from '../../components/admin/StudentsTab';
import ImagesTab from '../../components/admin/ImagesTab';
import VisibilityTab from '../../components/admin/VisibilityTab';
import DesignTab from '../../components/admin/DesignTab';
import { LeadsTab, AnalyticsTab } from '../../components/admin/LeadsAnalyticsTabs';
import LeadsInbox from '../../components/admin/LeadsInbox';
import AutoReplyTab from '../../components/admin/AutoReplyTab';
import EmailIdentitiesTab from '../../components/admin/EmailIdentitiesTab';
import TeamAccountsTab from '../../components/admin/TeamAccountsTab';
import AccountTab from '../../components/admin/AccountTab';
import TeamTab from '../../components/admin/TeamTab';
import LanguageManagement from '../../components/admin/LanguageManagement';
import MessagingAppsManagement from '../../components/admin/MessagingAppsManagement';
import AdminDashboardSummary from '../../components/admin/leads/AdminDashboardSummary';

// Import helpers
import {
  getFieldValue,
  createFieldSetter,
  getNestedFieldValue,
  createNestedFieldSetter,
  getArrayItemValue,
  createArrayItemSetter,
  createArrayItemAdder,
  createArrayItemRemover,
  createSimpleFieldUpdater,
  createAnalyticsUpdater,
  createDesignUpdater,
  createLeadRoutingUpdater,
  createAutoReplyUpdater,
  createAutoReplyPathUpdater
} from '../../utils/adminContentHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { admin, logout, loading: authLoading, mustChangePassword, changePassword, updateProfile } = useAdminAuth();
  const navigate = useNavigate();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('content');
  const [leadsSubTab, setLeadsSubTab] = useState('inbox');
  const [unreadLeads, setUnreadLeads] = useState(0);
  const [activeLang, setActiveLang] = useState('en');
  const [teamAccountsFocusRegion, setTeamAccountsFocusRegion] = useState(null);
  
  // Content state
  const [content, setContent] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  
  // Password modal state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [passwordError, setPasswordError] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Profile state
  const [profileForm, setProfileForm] = useState({ name: '', email: '' });
  const [savingProfile, setSavingProfile] = useState(false);

  // Create memoized helper functions
  const setFieldValue = useMemo(() => createFieldSetter(setContent), []);
  const setNestedFieldValue = useMemo(() => createNestedFieldSetter(setContent), []);
  const setArrayItemValue = useMemo(() => createArrayItemSetter(setContent), []);
  const addArrayItem = useMemo(() => createArrayItemAdder(setContent), []);
  const removeArrayItem = useMemo(() => createArrayItemRemover(setContent), []);
  const updateSimpleField = useMemo(() => createSimpleFieldUpdater(setContent), []);
  const updateAnalytics = useMemo(() => createAnalyticsUpdater(setContent), []);
  const updateDesign = useMemo(() => createDesignUpdater(setContent), []);
  const updateLeadRouting = useMemo(() => createLeadRoutingUpdater(setContent), []);
  const updateAutoReply = useMemo(() => createAutoReplyUpdater(setContent), []);
  const updateAutoReplyPath = useMemo(() => createAutoReplyPathUpdater(setContent), []);

  // Wrapped getters for current language
  const getField = useCallback((obj, field) => getFieldValue(obj, field, activeLang), [activeLang]);
  const getNestedField = useCallback(
    (section, subsection, field) => getNestedFieldValue(content, section, subsection, field, activeLang),
    [content, activeLang]
  );
  const getArrayItem = useCallback(
    (section, field, index, key) => getArrayItemValue(content, section, field, index, key, activeLang),
    [content, activeLang]
  );

  // Fetch content
  const fetchContent = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/content`, { withCredentials: true });
      setContent(response.data);
    } catch (error) {
      if (error.response?.status === 401) navigate('/admin/login');
    }
  }, [navigate]);

  // Fetch images
  const fetchImages = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/images`, { withCredentials: true });
      setImages(response.data.images || []);
    } catch (err) {
      // Images fetch failed - use placeholders
      setImages([]);
    }
  }, []);

  // Poll unread lead count for the "Leads" tab badge
  const fetchUnreadLeads = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/leads/unread-count`, { withCredentials: true });
      setUnreadLeads(res.data.unread || 0);
    } catch (err) {
      // Silent — badge just stays at its last known value
    }
  }, []);

  // Initial load — runs once per distinct login (keyed on username), never
  // again on the 20s session-refresh poll, so the active tab and initial
  // content fetch aren't repeatedly reset while the user is working.
  const initializedUsernameRef = useRef(null);
  useEffect(() => {
    if (!authLoading && !admin) {
      navigate('/admin/login');
    }
  }, [admin, authLoading, navigate]);

  useEffect(() => {
    if (!admin || initializedUsernameRef.current === admin.username) {
      if (!admin) initializedUsernameRef.current = null; // logged out — next login re-initializes fresh
      return;
    }
    initializedUsernameRef.current = admin.username;
    Promise.all([fetchContent(), fetchImages()]).finally(() => setLoading(false));
    if (admin.role === 'manager' || admin.role === 'agent') {
      setActiveTab('leads');
    }
  }, [admin, fetchContent, fetchImages]);

  // Keep the Account tab's profile form in sync with the session (e.g. if
  // Admin edits this user's name/email elsewhere) — only re-syncs when the
  // committed name/email actually changes, not on every 20s poll tick.
  useEffect(() => {
    if (!admin) return;
    setProfileForm({ name: admin.name || '', email: admin.email || '' });
  }, [admin?.name, admin?.email]);

  // Poll unread leads every 30s so the badge stays fresh across tabs
  useEffect(() => {
    if (!admin) return;
    fetchUnreadLeads();
    const interval = setInterval(fetchUnreadLeads, 30000);
    return () => clearInterval(interval);
  }, [admin, fetchUnreadLeads]);

  // Handle must change password (dismissible via "Remind me later")
  const [passwordModalDismissed, setPasswordModalDismissed] = useState(false);
  useEffect(() => {
    if (mustChangePassword && !showPasswordModal && !passwordModalDismissed) {
      setShowPasswordModal(true);
    }
  }, [mustChangePassword, showPasswordModal, passwordModalDismissed]);

  // Handlers
  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/content`, { content }, { withCredentials: true });
      toast.success('Changes published successfully!');
    } catch (err) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = useCallback((key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // File handling
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer?.files?.[0]) await uploadFile(e.dataTransfer.files[0]);
  }, []);

  const handleFileSelect = useCallback(async (e) => {
    if (e.target.files?.[0]) await uploadFile(e.target.files[0]);
  }, []);

  const uploadFile = async (file) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/upload-image`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Image uploaded!');
      fetchImages();
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const uploadAndGetUrl = async (file) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image');
      return null;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/upload-image`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Image uploaded!');
      fetchImages();
      return response.data.url;
    } catch (err) {
      toast.error('Upload failed');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const deleteImage = async (filename) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/images/${filename}`, { withCredentials: true });
      toast.success('Deleted!');
      fetchImages();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const copyImageUrl = useCallback((url) => {
    navigator.clipboard.writeText(url.startsWith('http') ? url : `${window.location.origin}${url}`);
    toast.success('URL copied!');
  }, []);

  // Password change handler
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    
    if (passwordForm.new !== passwordForm.confirm) {
      setPasswordError('Passwords do not match');
      return;
    }
    if (passwordForm.new.length < 8) {
      setPasswordError('Minimum 8 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      const result = await changePassword(passwordForm.current, passwordForm.new);
      if (result.success) {
        toast.success('Password changed!');
        setShowPasswordModal(false);
        setPasswordForm({ current: '', new: '', confirm: '' });
      } else {
        setPasswordError(result.error || 'Failed');
      }
    } catch (err) {
      setPasswordError(err.response?.data?.detail || 'Failed');
    } finally {
      setChangingPassword(false);
    }
  };

  // Profile update handler
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const result = await updateProfile(profileForm);
      if (result.success) {
        toast.success('Profile updated!');
      } else {
        toast.error(result.error || 'Failed');
      }
    } catch (err) {
      toast.error('Failed');
    } finally {
      setSavingProfile(false);
    }
  };

  // Loading state
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900" />
      </div>
    );
  }

  if (!content) return null;

  return (
    <div className="min-h-screen bg-slate-100">
      <PasswordModal
        show={showPasswordModal}
        mustChange={mustChangePassword}
        passwordForm={passwordForm}
        setPasswordForm={setPasswordForm}
        passwordError={passwordError}
        changingPassword={changingPassword}
        onSubmit={handlePasswordChange}
        onClose={() => { setShowPasswordModal(false); setPasswordModalDismissed(true); }}
      />

      <AdminHeader
        admin={admin}
        saving={saving}
        onSave={handleSave}
        onLogout={handleLogout}
      />

      {admin?.role === 'admin' && (
        <AdminDashboardSummary
          onGoToRouting={() => { setActiveTab('leads'); setLeadsSubTab('routing'); }}
          onGoToLeadRequests={() => { setActiveTab('leads'); setLeadsSubTab('inbox'); }}
        />
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <AdminTabs activeTab={activeTab} setActiveTab={setActiveTab} badges={{ leads: unreadLeads }} role={admin?.role || 'admin'} />

        {activeTab === 'content' && (
          <ContentTab
            content={content}
            activeLang={activeLang}
            setActiveLang={setActiveLang}
            expandedSections={expandedSections}
            toggleSection={toggleSection}
            getFieldValue={getField}
            setFieldValue={(section, field, value) => setFieldValue(section, field, value, activeLang)}
            getNestedFieldValue={getNestedField}
            setNestedFieldValue={(section, subsection, field, value) => 
              setNestedFieldValue(section, subsection, field, value, activeLang)}
            getArrayItemValue={getArrayItem}
            setArrayItemValue={(section, field, index, key, value) => 
              setArrayItemValue(section, field, index, key, value, activeLang)}
            addArrayItem={addArrayItem}
            removeArrayItem={removeArrayItem}
          />
        )}

        {activeTab === 'students' && <StudentsTab />}

        {activeTab === 'team' && <TeamTab />}

        {activeTab === 'languages' && <LanguageManagement />}

        {activeTab === 'messagingApps' && <MessagingAppsManagement />}

        {activeTab === 'images' && (
          <ImagesTab
            content={content}
            images={images}
            updateSimpleField={updateSimpleField}
            uploadAndGetUrl={uploadAndGetUrl}
            handleDrag={handleDrag}
            handleDrop={handleDrop}
            handleFileSelect={handleFileSelect}
            deleteImage={deleteImage}
            copyImageUrl={copyImageUrl}
            uploading={uploading}
            dragActive={dragActive}
          />
        )}

        {activeTab === 'visibility' && (
          <VisibilityTab content={content} updateSimpleField={updateSimpleField} />
        )}

        {activeTab === 'design' && (
          <DesignTab content={content} updateDesign={updateDesign} />
        )}

        {activeTab === 'leads' && (
          <div>
            <div className="flex gap-1 mb-4 border-b border-slate-200">
              {[
                { id: 'inbox', label: 'Inbox' },
                ...(admin?.role !== 'agent' ? [{ id: 'routing', label: 'Routing Settings' }] : []),
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setLeadsSubTab(t.id)}
                  data-testid={`leads-subtab-${t.id}`}
                  className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                    leadsSubTab === t.id ? 'text-slate-900 border-slate-900' : 'text-slate-500 border-transparent hover:text-slate-700'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            {leadsSubTab === 'inbox' && <LeadsInbox onUnreadChange={fetchUnreadLeads} role={admin?.role || 'admin'} />}
            {leadsSubTab === 'routing' && admin?.role !== 'agent' && (
              <LeadsTab
                content={content}
                updateLeadRouting={updateLeadRouting}
                role={admin?.role || 'admin'}
                onFixRegionOwner={(region) => { setTeamAccountsFocusRegion(region); setActiveTab('teamAccounts'); }}
              />
            )}
          </div>
        )}

        {activeTab === 'autoReply' && (
          <AutoReplyTab
            content={content}
            updateAutoReply={updateAutoReply}
            updateAutoReplyPath={updateAutoReplyPath}
          />
        )}

        {activeTab === 'emailSettings' && <EmailIdentitiesTab role={admin?.role || 'admin'} />}

        {activeTab === 'teamAccounts' && (
          <TeamAccountsTab currentRole={admin?.role} currentRegions={admin?.regions || []} focusRegion={teamAccountsFocusRegion} onFocusRegionClear={() => setTeamAccountsFocusRegion(null)} />
        )}

        {activeTab === 'analytics' && (
          <AnalyticsTab content={content} updateAnalytics={updateAnalytics} />
        )}

        {activeTab === 'account' && (
          <AccountTab
            profileForm={profileForm}
            setProfileForm={setProfileForm}
            handleProfileUpdate={handleProfileUpdate}
            savingProfile={savingProfile}
            setShowPasswordModal={setShowPasswordModal}
          />
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
